import { LayoutConfig } from '../../../models/user-config/DisplayModeConfig';
import DatasetsLayoutNode from '../dataset-tree/layout/DatasetsLayoutNode';
import DatasetsLayoutNodeVisitor from '../dataset-tree/layout/DatasetsLayoutNodeVisitor';
import HorizontalStackLayoutNode from '../dataset-tree/layout/HorizontalStackLayoutNode';
import SingleDatasetLayoutNode from '../dataset-tree/layout/SingleDatasetLayoutNode';
import TabsLayoutNode from '../dataset-tree/layout/TabsLayoutNode';
import VerticalStackLayoutNode from '../dataset-tree/layout/VerticalStackLayoutNode';
import DisplayModeDatasetTreeNode from '../dataset-tree/logical/DisplayModeDatasetTreeNode';

type DimensionReducer = (first: number, second: number) => number;

const sumReducer: DimensionReducer = (first, second) => first + second;
const maxReducer: DimensionReducer = (first, second) => Math.max(first, second);

/**
 * {@link DatasetsLayoutNode} tree visitor that implements calculation of
 * required column and row dimensions to distribute screen space evenly
 * between the layout elements with regard to the count of leaf elements
 * (that is, actual datasets).
 */
class LayoutNodeDimensionVisitor implements DatasetsLayoutNodeVisitor {
  private cols = 0;
  private rows = 0;
  private configHeight: number | undefined;
  private configWidth: number | undefined;

  constructor(private layoutConfig?: LayoutConfig) {}

  /**
   * Returns the calculated maximal count of datasets across the horizontal
   * axis.
   */
  public getLayoutCols(): number {
    return this.cols;
  }

  /**
   * Returns the calculated maximal count of datasets across the vertical
   * axis.
   */
  public getLayoutRows(): number {
    return this.rows;
  }

  /**
   * Returns first found dataset height percentage from config
   */
  public getConfigHeight(): number | undefined {
    return this.configHeight;
  }

  /**
   * Returns first found dataset width percentage from config
   */
  public getConfigWidth(): number | undefined {
    return this.configWidth;
  }

  public visitSingleDataset(node: SingleDatasetLayoutNode): void {
    const dataset = node.getDataset();
    let sizes: { panelHSize?: number; panelVSize?: number } | undefined;
    if (dataset instanceof DisplayModeDatasetTreeNode) {
      const datasetName = dataset.getDatasetName();
      sizes = this.layoutConfig?.datasets?.[datasetName];
    }
    this.cols = 1;
    this.rows = 1;
    this.configHeight = sizes?.panelVSize;
    this.configWidth = sizes?.panelHSize;
  }

  public visitHorizontalStack(node: HorizontalStackLayoutNode): void {
    this.measureDimensions(node, sumReducer, maxReducer);
  }

  public visitTabs(node: TabsLayoutNode): void {
    this.measureDimensions(node, maxReducer, maxReducer);
  }

  public visitVerticalStack(node: VerticalStackLayoutNode): void {
    this.measureDimensions(node, maxReducer, sumReducer);
  }

  private measureDimensions(
    node: DatasetsLayoutNode,
    colsReducer: DimensionReducer,
    rowsReducer: DimensionReducer
  ): void {
    let isFirstIteration = true;
    let cols = 0;
    let rows = 0;

    for (const child of node.getChildren()) {
      const childVisitor = new LayoutNodeDimensionVisitor(this.layoutConfig);

      child.acceptVisitor(childVisitor);

      const childCols = childVisitor.getLayoutCols();
      const childRows = childVisitor.getLayoutRows();

      const isDatasetOrTab =
        child instanceof SingleDatasetLayoutNode ||
        child instanceof TabsLayoutNode;

      if (isDatasetOrTab || child instanceof HorizontalStackLayoutNode) {
        this.configHeight = this.configHeight || childVisitor.getConfigHeight();
      }
      if (isDatasetOrTab || child instanceof VerticalStackLayoutNode) {
        this.configWidth = this.configWidth || childVisitor.getConfigWidth();
      }

      if (isFirstIteration) {
        cols = childCols;
        rows = childRows;
        isFirstIteration = false;
      } else {
        cols = colsReducer(cols, childCols);
        rows = rowsReducer(rows, childRows);
      }
    }

    this.cols = cols;
    this.rows = rows;
  }
}

export default LayoutNodeDimensionVisitor;
